/**
 * Static content for prop-firm SEO articles.
 * Each article: ~600-800 words in French across sections.
 * Slugs match the FIRM IDs used in PropFirmCompare.jsx.
 */

const buildBase = (domain) => `https://${domain}/?ref=REF_PLACEHOLDER`;

export const ARTICLES = {
  ftmo: {
    name: "FTMO",
    flag: "🇨🇿",
    rating: 4.7,
    price100k: 540,
    split: "80-90%",
    baseUrl: buildBase("ftmo.com"),
    metaDescription: "Avis complet FTMO 2026 : règles, drawdown, split des profits, délais de payout. Faut-il choisir FTMO pour son premier challenge prop firm ?",
    verdict: "FTMO reste la référence historique du prop trading en 2026. Basée à Prague depuis 2015, la firme a payé plus de 200 millions de dollars à ses traders. Ses règles sont strictes mais parfaitement claires, ses payouts fiables, et son écosystème (formation, statistiques, communauté) est le plus mature du marché. Idéal pour un premier challenge sérieux, moins compétitif sur le prix.",
    intro: "FTMO est une des toutes premières prop firms modernes, fondée à Prague en 2015 par une équipe de traders professionnels. Leur modèle : vous payez un challenge (à partir de 89 € pour un compte 10k), vous démontrez votre capacité à trader avec discipline pendant 30 jours (challenge) puis 60 jours (verification), et si vous respectez toutes les règles, ils vous confient un compte réel avec un split de 80% (montable à 90% après plusieurs paiements). En 2026, FTMO a payé plus de 200 millions de dollars à sa communauté — un record de transparence dans un secteur souvent opaque.",
    rules: [
      { label: "Drawdown quotidien", value: "5% max" },
      { label: "Drawdown total",     value: "10% max" },
      { label: "Objectif Challenge", value: "10%" },
      { label: "Objectif Verification", value: "5%" },
      { label: "Jours min de trading", value: "4" },
      { label: "Durée max",         value: "30j + 60j" },
      { label: "Consistency rule",   value: "Non" },
      { label: "Weekend holding",    value: "Autorisé" },
    ],
    strengths: [
      "Réputation solide depuis 2015 — la firme la plus mature du marché.",
      "Payouts fiables toutes les 2 semaines, avec possibilité de premier retrait dès 14 jours.",
      "Split croissant jusqu'à 90% pour les traders réguliers (via le programme scaling).",
      "Application dashboard riche : statistiques détaillées, feedback quotidien, calendrier économique intégré.",
      "Aucune restriction sur les stratégies : news trading autorisé, weekend holding autorisé, EAs autorisés.",
      "Communauté active (Discord officiel, meetups annuels, académie interne gratuite).",
    ],
    weaknesses: [
      "Prix plus élevé que la concurrence (~540 $ pour un 100k contre 260 $ chez The 5%ers).",
      "2 étapes obligatoires : impossible de passer directement en funded sans validation.",
      "Le drawdown quotidien de 5% peut sembler serré pour un swing trader qui laisse courir.",
    ],
    comparison: "Face à FundedNext ou FundingPips (payouts hebdomadaires, moins chers), FTMO joue la carte de la maturité et de la crédibilité. Face à The 5%ers (moins cher, drawdown plus strict à 4%), FTMO offre plus de latitude technique mais coûte plus. Face à FTUK (instant funding, pas de challenge), FTMO impose un cheminement plus long mais plus sécurisant pour le trader qui n'a pas encore une méthode rodée. En résumé : FTMO est le choix par défaut pour un trader intermédiaire qui veut construire une réputation et payer une fois pour être tranquille longtemps.",
    audience: "FTMO est idéale pour un trader intermédiaire à confirmé qui privilégie la sécurité juridique (siège UE, régulation KYC), la qualité de service, et une communauté active. C'est également le meilleur choix pour un swing trader qui trade quelques positions par semaine avec discipline. En revanche, si vous êtes scalpeur ou si votre budget est limité, regardez plutôt FundingPips ou TopStep.",
    faq: [
      { q: "FTMO est-elle disponible depuis la France ?",
        a: "Oui, FTMO accepte les résidents français. La firme est basée en République tchèque (UE) donc soumise aux règles européennes. Vous pouvez trader avec FTMO en respectant la fiscalité française sur les BNC (Bénéfices Non Commerciaux) ou en micro-entreprise selon votre régime." },
      { q: "Combien de temps pour passer le challenge ?",
        a: "Vous avez 30 jours pour la Phase 1 (Challenge, objectif 10%) puis 60 jours pour la Phase 2 (Verification, objectif 5%). En moyenne, un trader préparé passe le tout en 3 à 6 semaines. Il n'y a pas de bonus pour aller vite : mieux vaut respecter les règles que se précipiter." },
      { q: "Puis-je utiliser un EA (robot) FTMO ?",
        a: "Oui, FTMO autorise les Expert Advisors (EAs) et le trading algorithmique tant que la stratégie est votre propriété. Les copy-trading depuis un autre compte et les scalping HFT sont interdits. Vérifiez toujours les Terms of Use avant d'utiliser un bot que vous n'avez pas codé." },
      { q: "Que se passe-t-il si je viole la règle de drawdown ?",
        a: "Le compte est immédiatement clôturé sans possibilité de reprise sur le même challenge. Vous devez racheter un nouveau challenge. C'est pourquoi ANATRADEX vous alerte dès que vous atteignez 80% de la limite journalière — pour vous arrêter avant de casser le compte." },
      { q: "Le split est-il vraiment de 90% ?",
        a: "Le split de base est 80%. Après plusieurs paiements consécutifs, FTMO propose de passer à 90% via leur programme scaling. Les 10% que garde FTMO couvrent leurs coûts opérationnels et leurs marges — c'est un modèle transparent." },
    ],
  },

  fundednext: {
    name: "FundedNext",
    flag: "🌐",
    rating: 4.5,
    price100k: 549,
    split: "80-95%",
    baseUrl: buildBase("fundednext.com"),
    metaDescription: "FundedNext avis 2026 : est-ce que la firme aux payouts hebdomadaires et au split 95% est vraiment fiable ? Analyse complète.",
    verdict: "FundedNext s'est imposée en 2 ans comme la sérieuse alternative à FTMO. Ses payouts hebdomadaires et son split jusqu'à 95% en font le choix privilégié des scalpeurs et day-traders qui veulent monétiser rapidement leurs performances. Le programme Stellar 1-Step (un seul examen, drawdown plus strict) attire ceux qui veulent aller vite.",
    intro: "FundedNext a été lancée en 2022 par des vétérans du prop trading, à Dubaï. En moins de 3 ans, la firme a payé plus de 100 millions de dollars et compte plus de 100 000 traders actifs. Son positionnement : agilité maximale — payouts hebdomadaires, plusieurs programmes (Evaluation 2-Step, Stellar 1-Step, Express), et un split qui peut monter à 95% pour les traders réguliers.",
    rules: [
      { label: "Drawdown quotidien", value: "5% (Evaluation) / 3% (Stellar 1-Step)" },
      { label: "Drawdown total",     value: "10% / 6% selon programme" },
      { label: "Objectif",           value: "10% (2 étapes) ou 10% (1 étape)" },
      { label: "Jours min",          value: "5" },
      { label: "Payout",             value: "Hebdomadaire (dès 7 jours)" },
      { label: "Consistency rule",   value: "Optionnelle" },
      { label: "Weekend holding",    value: "Autorisé" },
      { label: "News trading",       value: "Autorisé" },
    ],
    strengths: [
      "Payouts hebdomadaires — cash-flow beaucoup plus rapide que la concurrence.",
      "Split jusqu'à 95% (le meilleur du marché avec The 5%ers).",
      "Programme Stellar 1-Step pour éviter la Phase 2 (idéal si vous êtes confiant).",
      "Plusieurs plateformes supportées : MT4, MT5, cTrader.",
      "Support client réactif 24/7 en anglais et français.",
    ],
    weaknesses: [
      "Historique plus court (fondée 2022) — moins de recul que FTMO.",
      "Règles évolutives (mises à jour plusieurs fois par an) — vérifiez avant chaque achat.",
      "Le drawdown 3% de Stellar 1-Step est très serré pour un trader intraday agressif.",
    ],
    comparison: "Face à FTMO (2 étapes obligatoires, 80% split de base), FundedNext offre des payouts 2x plus rapides et un split potentiellement plus élevé. Face à The 5%ers (payouts mensuels), FundedNext est le choix des impatients. Face à FundingPips (concurrent direct), FundedNext a plus de crédibilité et plus de plateformes.",
    audience: "FundedNext est parfait pour un scalpeur ou day-trader qui veut voir ses gains arriver toutes les semaines, et qui ne veut pas s'embêter avec la Phase 2. Le programme Stellar 1-Step est agressif — à réserver aux traders confirmés. Pour un premier challenge, préférez le programme Evaluation classique.",
    faq: [
      { q: "Quelle est la différence entre Evaluation et Stellar 1-Step ?",
        a: "Evaluation est un challenge classique 2 étapes (10% puis 5%), drawdown 5%/10%. Stellar 1-Step est un examen unique de 10%, drawdown quotidien 3% et total 6% — plus rapide mais plus strict." },
      { q: "Les payouts hebdomadaires sont-ils vraiment tous les 7 jours ?",
        a: "Oui, dès que vous êtes en compte funded, vous pouvez demander un retrait tous les 7 jours (payé sous 24-48h généralement)." },
      { q: "Puis-je faire du swing trading (positions plusieurs jours) ?",
        a: "Oui, le weekend holding est autorisé sur tous les programmes FundedNext, y compris pendant les weekends risqués (dimanche soir Asie)." },
      { q: "FundedNext est-elle régulée ?",
        a: "FundedNext opère depuis Dubaï via une structure business standard. La firme n'est pas un broker régulé (elle ne prend pas les dépôts de traders) — c'est un modèle de prop firm classique." },
      { q: "Peut-on cumuler plusieurs comptes FundedNext ?",
        a: "Oui, jusqu'à 3 comptes funded en simultané pour un total de 400 000 $ de capital géré. Vous pouvez cumuler les payouts." },
    ],
  },

  the5ers: {
    name: "The 5%ers",
    flag: "5️⃣",
    rating: 4.4,
    price100k: 260,
    split: "80-100%",
    baseUrl: buildBase("the5ers.com"),
    metaDescription: "The 5%ers avis 2026 : la prop firm au split 100% et au programme de scaling infini. Faut-il choisir le Bootcamp ou le programme Aggressive ?",
    verdict: "The 5%ers est la prop firm des traders patients. Fondée en 2016, elle propose un modèle Bootcamp (challenge en 1 étape, prix bas, drawdown 4%) et un modèle Aggressive plus classique. Le vrai atout : le programme de scaling qui vous fait monter jusqu'à 100% de split et 4 millions de dollars de capital. Réservé aux traders swing rigoureux.",
    intro: "The 5%ers est basée en Israël depuis 2016. Leur philosophie : construire des traders sur le long terme via un scaling agressif. Contrairement à FTMO ou FundedNext, The 5%ers vous fait passer un Bootcamp (challenge à petit prix, 6% de profit, drawdown 4%, minimum 10 jours de trading) et vous récompense d'année en année si vous performez.",
    rules: [
      { label: "Drawdown total",     value: "4% (Bootcamp) / 10% (Aggressive)" },
      { label: "Drawdown quotidien", value: "4% / 5%" },
      { label: "Objectif",           value: "6% (Bootcamp) / 10% (Aggressive)" },
      { label: "Jours min",          value: "10" },
      { label: "Consistency",        value: "Obligatoire" },
      { label: "Scaling",            value: "Jusqu'à 4M$ et 100% split" },
      { label: "Payout",             value: "Mensuel" },
      { label: "Weekend holding",    value: "Autorisé" },
    ],
    strengths: [
      "Programme scaling unique : jusqu'à 100% de split après plusieurs paliers.",
      "Bootcamp très abordable (~260 $ pour 100k) — le meilleur rapport prix/capital du marché.",
      "Historique de 8+ ans, l'une des plus anciennes prop firms du marché.",
      "Focus sur la construction long terme du trader, pas les allers-retours de challenges.",
    ],
    weaknesses: [
      "Drawdown 4% très strict — impossible d'être un day-trader agressif.",
      "Consistency rule obligatoire (pas plus de 50% du profit sur une seule journée).",
      "Payouts mensuels seulement (plus lent que la concurrence).",
    ],
    comparison: "Face à FTMO, The 5%ers est moitié moins cher au challenge mais avec un drawdown 2x plus strict. Face à FundedNext, The 5%ers propose payouts plus lents mais scaling plus généreux à long terme. C'est le contraire de TopStep (futures, day trading) — The 5%ers pousse au swing sain.",
    audience: "Idéale pour un swing trader qui laisse tourner ses positions, respecte un plan strict, et veut construire une carrière long terme dans le prop trading. Éviter si vous êtes scalpeur, si vous avez besoin de liquidité rapide (payouts hebdo), ou si vous ne supportez pas les règles de consistency.",
    faq: [
      { q: "Qu'est-ce que la consistency rule ?",
        a: "Aucune journée ne doit représenter plus de 50% de vos profits cumulés. Cela évite les stratégies 'tout ou rien' et pousse à trader avec régularité." },
      { q: "Le programme Bootcamp est-il vraiment gagnable ?",
        a: "Oui, mais il est calibré pour les traders patients. Avec 6% d'objectif en 10 jours minimum et un DD de 4%, vous devez tenir 6-15% par mois avec discipline. Environ 15% des traders passent le Bootcamp au premier essai." },
      { q: "Puis-je perdre le compte funded après une mauvaise journée ?",
        a: "Non — la DD de 4% est appliquée dans le Bootcamp uniquement. Une fois funded, les règles se relâchent progressivement selon vos paliers de scaling." },
    ],
  },

  myforexfunds: {
    name: "MyForexFunds",
    flag: "💱",
    rating: 3.9,
    price100k: 599,
    split: "75-90%",
    baseUrl: buildBase("myforexfunds.com"),
    metaDescription: "MyForexFunds avis 2026 : après les controverses juridiques de 2023, la firme a-t-elle retrouvé sa crédibilité ? Notre analyse honnête.",
    verdict: "MyForexFunds (MFF) a été l'une des plus grandes prop firms du monde en 2022-2023 avant d'être suspendue temporairement par la CFTC pour des soupçons de manipulation. La firme a repris ses activités début 2024 avec de nouveaux propriétaires et une nouvelle transparence. Prudence : historiquement innovante, mais crédibilité encore fragile en 2026.",
    intro: "MyForexFunds fut une des étoiles montantes du prop trading en 2022, avec plus de 200 000 traders actifs et un programme Rapid Challenge très populaire (pas de min trading days). Fin août 2023, la CFTC américaine a suspendu leurs opérations pour investigation — les fonds des traders furent bloqués pendant plusieurs mois. La firme a repris début 2024 après restructuration.",
    rules: [
      { label: "Drawdown total",     value: "8%" },
      { label: "Drawdown quotidien", value: "5%" },
      { label: "Objectif",           value: "8%" },
      { label: "Jours min",          value: "0 (Rapid) ou 5 (Evaluation)" },
      { label: "Payout",             value: "Bi-hebdomadaire" },
      { label: "Weekend holding",    value: "Autorisé" },
    ],
    strengths: [
      "Rapid Challenge : aucun minimum de jours de trading, idéal pour scalpeurs.",
      "Prix relativement compétitif.",
      "Réouverture avec structure améliorée post-2024.",
    ],
    weaknesses: [
      "Historique récent entaché : suspension CFTC 2023, fonds bloqués aux traders.",
      "Crédibilité à reconstruire — préférez d'autres firmes pour vos premiers challenges.",
      "Support et communication moins mature que FTMO ou FundedNext.",
    ],
    comparison: "Face à ses concurrents historiques (FTMO, The 5%ers), MyForexFunds paye le prix de ses controverses. Face aux nouveaux entrants (FundingPips, E8), MFF n'apporte plus grand-chose de différenciant depuis sa restructuration.",
    audience: "MyForexFunds ne devrait être choisie que si vous êtes déjà familier avec la firme et acceptez le risque réputationnel. Pour un premier challenge en 2026, préférez FTMO, FundedNext ou The 5%ers.",
    faq: [
      { q: "Que s'est-il passé en 2023 avec la CFTC ?",
        a: "La CFTC (régulateur américain) a suspendu MyForexFunds en août 2023 pour soupçons de manipulation des ordres. Les traders ont dû attendre plusieurs mois pour récupérer leurs fonds. La firme a repris début 2024 sous une nouvelle structure." },
      { q: "Est-ce que MyForexFunds paye vraiment aujourd'hui ?",
        a: "Oui, la firme paye à nouveau ses traders depuis 2024. Les payouts sont bi-hebdomadaires. Mais les délais sont parfois plus longs qu'annoncés." },
    ],
  },

  topstep: {
    name: "TopStep",
    flag: "🎯",
    rating: 4.6,
    price100k: 165,
    split: "90-100%",
    baseUrl: buildBase("topstep.com"),
    metaDescription: "TopStep avis 2026 : la référence du prop trading sur futures (CME). Faut-il choisir TopStep pour trader les indices ou les métaux ?",
    verdict: "TopStep est LA référence historique du prop trading sur futures. Fondée en 2010, la firme se concentre exclusivement sur les instruments CME (ES, NQ, GC, CL, YM, RTY). Si vous êtes day trader sur futures, TopStep est incontournable — mais si vous voulez trader le forex ou la crypto, cherchez ailleurs.",
    intro: "TopStep, basée à Chicago, est la doyenne du prop trading. Créée en 2010, elle a démocratisé l'accès aux futures avec son Combine (challenge de 1 étape) puis son Express Funded Account. La firme est proche des courtiers CME (Ninja Trader, TradingView) et propose une expérience très 'pro' à un prix très bas (~165 $ pour un compte 100k).",
    rules: [
      { label: "Drawdown quotidien", value: "2% (très strict)" },
      { label: "Drawdown total",     value: "3%" },
      { label: "Objectif",           value: "6% en 10 jours minimum" },
      { label: "Weekend holding",    value: "INTERDIT" },
      { label: "Payout",             value: "Sous 8 jours" },
      { label: "Instruments",        value: "Futures CME uniquement" },
    ],
    strengths: [
      "Pionnier depuis 2010 — écosystème le plus mature sur futures.",
      "Prix bas (~165 $ pour 100k) — imbattable.",
      "Payouts rapides (7-10 jours).",
      "Split jusqu'à 100% dans certaines conditions.",
      "Communauté trader active + coaching offert.",
    ],
    weaknesses: [
      "Futures CME uniquement — pas de forex, crypto, actions.",
      "Weekend holding INTERDIT — vous devez tout fermer avant vendredi 15h EST.",
      "Drawdown quotidien 2% très serré — un stop-loss mal placé et c'est game over.",
    ],
    comparison: "TopStep est un monde à part : elle ne se compare pas directement à FTMO/FundedNext (forex/crypto). Pour un trader indices/matières premières, c'est la meilleure option. Pour un forex trader, c'est incompatible.",
    audience: "Day-traders sur ES/NQ (S&P 500 futures) et matières premières (or, pétrole). Incompatible avec swing trading (pas de weekend holding). Incompatible avec forex/crypto.",
    faq: [
      { q: "TopStep permet-elle de trader le forex ?",
        a: "Non, TopStep se concentre exclusivement sur les futures CME. Pour le forex, choisissez FTMO ou FundedNext." },
      { q: "Pourquoi TopStep est-elle si peu chère ?",
        a: "Le prix bas (~165 $ pour 100k) reflète le modèle historique de la firme, qui a commencé avant la vague récente de prop firms forex. Les marges sur futures sont plus prévisibles." },
    ],
  },

  e8: {
    name: "E8 Funding",
    flag: "8️⃣",
    rating: 4.2,
    price100k: 588,
    split: "80%",
    baseUrl: buildBase("e8markets.com"),
    metaDescription: "E8 Funding avis 2026 : le programme 8-Check pour rescale votre compte funded. Analyse détaillée.",
    verdict: "E8 Funding est une prop firm sérieuse mais peu différenciée en 2026. Son atout unique est le programme 8-Check qui permet de rescale votre compte funded jusqu'à 800 000 $. Split fixe à 80%, prix moyen, support correct. Pas de red flag, mais pas d'atout majeur non plus.",
    intro: "E8 Funding (E8 Markets) est apparue en 2022. Basée en République tchèque comme FTMO, elle propose un modèle classique 2 étapes. Sa particularité : le programme 8-Check qui vous permet de rescale votre compte funded à condition de rester profitable sur 8 périodes consécutives.",
    rules: [
      { label: "Drawdown total",     value: "8%" },
      { label: "Drawdown quotidien", value: "5%" },
      { label: "Objectif",           value: "8% (Phase 1) puis 5% (Phase 2)" },
      { label: "Jours min",          value: "5" },
      { label: "Split",              value: "80% (fixe)" },
      { label: "Programme 8-Check",  value: "Rescale x8 = 800 000 $" },
    ],
    strengths: [
      "Programme 8-Check : rescale jusqu'à 800k$ pour les traders performants.",
      "Support réactif.",
      "Bonne stabilité opérationnelle depuis 2022.",
    ],
    weaknesses: [
      "Split fixe à 80% — pas de progression vers 90%.",
      "Prix moyen sans atout compétitif fort.",
      "Moins de communauté que FTMO ou FundedNext.",
    ],
    comparison: "Face à FTMO, E8 est légèrement moins cher mais offre moins d'atouts. Face à FundedNext, E8 a moins de flexibilité de payouts. Le programme 8-Check est unique mais n'attire que les traders vraiment sérieux dans la durée.",
    audience: "Trader qui veut un modèle 2 étapes classique et compte rescale son compte sur la durée. Sinon, préférez FTMO pour la sécurité ou FundedNext pour la rapidité.",
    faq: [
      { q: "Comment fonctionne le 8-Check ?",
        a: "Après 8 périodes de profit consécutives (chaque période étant définie par E8), vous pouvez rescale votre compte funded. Vous passez ainsi de 100k$ à 200k$, puis 400k$, jusqu'à 800k$." },
    ],
  },

  ftuk: {
    name: "FTUK",
    flag: "🇬🇧",
    rating: 4.0,
    price100k: 599,
    split: "80%",
    baseUrl: buildBase("ftuk.com"),
    metaDescription: "FTUK avis 2026 : la seule prop firm qui propose de l'instant funding sans challenge. Est-ce trop beau pour être vrai ?",
    verdict: "FTUK est unique : elle propose de l'instant funding, ce qui signifie que vous êtes directement sur un compte réel dès l'achat, sans passer par un challenge. Pratique si vous êtes confiant et pressé, mais le prix est élevé et le split reste modeste à 80%. À utiliser en complément d'un compte challenge classique.",
    intro: "FTUK (Funded Trader UK) est basée à Londres et propose depuis 2020 un modèle rare : l'instant funding. Vous payez, vous tradez, vous êtes payé. Pas de Phase 1, pas de Phase 2. Le trade-off : le prix est plus élevé que la moyenne et le split reste à 80%.",
    rules: [
      { label: "Modèle",             value: "Instant Funding (0 étape)" },
      { label: "Drawdown total",     value: "10%" },
      { label: "Drawdown quotidien", value: "5%" },
      { label: "Objectif",           value: "10% (pour scaling)" },
      { label: "Split",              value: "80%" },
      { label: "Payout",             value: "Mensuel" },
    ],
    strengths: [
      "Instant funding : trader direct sur compte réel dès l'achat.",
      "Pas de challenge à répéter en cas d'échec.",
      "Régulée UK (structure business Londres).",
    ],
    weaknesses: [
      "Prix élevé pour ce qui est en fait un compte de trading simulé.",
      "Split modeste (80%).",
      "Payouts mensuels seulement.",
    ],
    comparison: "FTUK est une catégorie à part. Face aux challenges classiques (FTMO, FundedNext), FTUK économise le temps mais coûte plus cher par dollar de capital géré.",
    audience: "Trader confirmé qui veut éviter le processus de challenge et être productif immédiatement. À éviter si vous débutez — le challenge FTMO/FundedNext est aussi une occasion d'apprendre la discipline.",
    faq: [
      { q: "Est-ce que FTUK utilise des vrais fonds ?",
        a: "Comme la plupart des prop firms modernes, FTUK opère en simulation avec des payouts réels sur les profits. Vous êtes sur un compte demo, mais les gains sont payés en cash." },
    ],
  },

  fundingpips: {
    name: "FundingPips",
    flag: "💧",
    rating: 4.3,
    price100k: 449,
    split: "80-90%",
    baseUrl: buildBase("fundingpips.com"),
    metaDescription: "FundingPips avis 2026 : l'outsider ambitieux au prix agressif et aux payouts hebdomadaires. Vaut-il vraiment le coup ?",
    verdict: "FundingPips est la surprise du secteur en 2023-2026. Prix agressif (~449 $ pour 100k), payouts hebdomadaires, plateformes multiples : la firme coche presque toutes les cases. Le seul bémol : historique court. Un excellent choix pour un deuxième compte ou pour tester le concept sans dépenser trop.",
    intro: "FundingPips est apparue en 2023 avec une proposition claire : casser les prix. À 449 $ pour un challenge 100k contre 540 $ chez FTMO, la firme mise sur le volume. Elle propose 2 étapes classiques avec drawdown standard et un split jusqu'à 90%.",
    rules: [
      { label: "Drawdown total",     value: "10%" },
      { label: "Drawdown quotidien", value: "5%" },
      { label: "Objectif",           value: "8% puis 5%" },
      { label: "Jours min",          value: "3" },
      { label: "Payout",             value: "Hebdomadaire" },
      { label: "Split",              value: "80-90%" },
    ],
    strengths: [
      "Prix agressif : ~20% moins cher que FTMO à capital équivalent.",
      "Payouts hebdomadaires — cash-flow rapide.",
      "Support multi-plateformes : MT4, MT5, cTrader.",
      "Croissance rapide, communauté grandissante.",
    ],
    weaknesses: [
      "Historique court (2023+) — moins de recul que FTMO.",
      "Support client parfois lent aux heures de pointe.",
    ],
    comparison: "FundingPips concurrence directement FundedNext. Les deux ont des atouts similaires (payouts rapides, multi-plateformes) mais FundingPips est moins cher tandis que FundedNext a plus de crédibilité.",
    audience: "Trader qui veut minimiser le coût du challenge tout en gardant les avantages payouts rapides. Idéal pour un deuxième compte ou pour tester le concept.",
    faq: [
      { q: "FundingPips est-elle fiable ?",
        a: "La firme paye ses traders depuis 2023 et a construit une réputation solide. Elle reste plus jeune que FTMO/FundedNext, mais aucun red flag notable en 2026." },
    ],
  },
};
